import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArcaneBlitzGameState,
  SuperpowerType,
  SUPERPOWERS,
  getMana,
  canActivateSuperpower,
} from '@/lib/arcane-blitz-engine';
import { Sparkles, Zap } from 'lucide-react';

interface ArcanePowerPanelProps {
  state: ArcaneBlitzGameState;
  playerColor: 'white' | 'black';
  onActivate: (type: SuperpowerType) => void;
  isPlayerTurn: boolean;
  disabledPowers?: SuperpowerType[];
}

export function ArcanePowerPanel({
  state,
  playerColor,
  onActivate,
  isPlayerTurn,
  disabledPowers = [],
}: ArcanePowerPanelProps) {
  const mana = getMana(state, playerColor);
  const maxMana = 10;
  const chargingType = state.chargingPower?.type;

  return (
    <Card className="border-teen-purple/30 bg-gradient-to-br from-violet-950/60 via-purple-900/40 to-indigo-950/60 backdrop-blur-sm">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-sm font-bold flex items-center gap-2 text-teen-cyan">
          <Sparkles className="w-4 h-4 animate-sparkle" />
          Arcane Powers
        </CardTitle>

        {/* Mana bar */}
        <div className="space-y-1 mt-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Zap className="w-3 h-3 text-teen-cyan" />
              Mana
            </span>
            <Badge
              variant="outline"
              className="text-xs border-teen-cyan/40 text-teen-cyan px-1.5 py-0"
            >
              {mana} / {maxMana}
            </Badge>
          </div>
          <div className="relative">
            <Progress
              value={(mana / maxMana) * 100}
              className="h-2 bg-violet-950/50 [&>div]:bg-gradient-to-r [&>div]:from-teen-cyan [&>div]:to-teen-purple"
            />
            {/* Mana glow overlay */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                boxShadow: `0 0 ${mana * 2}px hsl(var(--teen-cyan)/0.4)`,
              }}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-3 pb-3 space-y-1.5">
        {SUPERPOWERS.map(def => {
          const isExplicitlyDisabled = disabledPowers.includes(def.type);
          const canUse = isPlayerTurn && !isExplicitlyDisabled && canActivateSuperpower(state, def.type, playerColor);
          const isCharging = chargingType === def.type;

          return (
            <Button
              key={def.type}
              size="sm"
              variant={isCharging ? 'default' : 'outline'}
              disabled={(!canUse && !isCharging) || isExplicitlyDisabled}
              onClick={() => onActivate(def.type)}
              className={`
                w-full justify-start gap-2 text-xs h-auto py-2 px-2.5
                ${isCharging
                  ? 'bg-teen-purple text-white border-teen-purple animate-pulse shadow-[0_0_12px_hsl(var(--teen-purple)/0.6)]'
                  : canUse
                  ? 'border-teen-purple/40 text-foreground hover:bg-teen-purple/10 hover:border-teen-purple/60'
                  : 'border-border/30 text-muted-foreground opacity-50'
                }
              `}
            >
              <span className="text-base leading-none">{def.emoji}</span>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold truncate">{def.name}</span>
                  <Badge
                    variant="secondary"
                    className={`text-[10px] px-1 py-0 shrink-0 ${
                      canUse
                        ? 'bg-teen-cyan/20 text-teen-cyan border-teen-cyan/30'
                        : 'bg-muted/30 text-muted-foreground'
                    }`}
                  >
                    {def.cost}✨
                  </Badge>
                </div>
                {isCharging && (
                  <p className="text-[10px] text-teen-cyan mt-0.5 leading-tight">
                    🎯 Click target on board…
                  </p>
                )}
              </div>
            </Button>
          );
        })}

        {/* Event log */}
        {state.superPowerLog.length > 0 && (
          <div className="mt-2 pt-2 border-t border-teen-purple/20">
            <p className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">
              Power log
            </p>
            <ScrollArea className="h-20">
              <div className="space-y-0.5">
                {state.superPowerLog.map((entry, i) => (
                  <p key={i} className="text-[10px] text-teen-cyan/80 leading-tight">
                    {entry}
                  </p>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
